/**
 * Fragment shader liquid-morph (bentuk cair, tepi spektral prisma).
 *
 * Ditulis sebagai string TS, bukan berkas `.glsl?raw`: impor `?raw` hanya
 * dipahami Vite (dev), sedangkan build produksi memakai `bun build` yang
 * gagal meresolvenya. Isi string di bawah harus tetap GLSL apa adanya
 * (`#version` harus di awal string, tanpa baris kosong di depan).
 */
export const LIQUID_MORPH_FRAG = `#version 300 es
// liquid-morph (T6): orb yang SILUETNYA berubah seperti cairan. Port setia dari
// prototipe putaran 2 (_refs/jenna-orb/prototype/orb-liquid-morph-prototype.html).
// Bola SDF didisplasi lipatan sinus halus + noise domain-warped, di-raymarch
// (jumlah langkah adaptif per ukuran, uSteps). Palet = spektrum prisma TETAP
// (bukan dari warna admin). Keluaran PREMULTIPLIED (premultipliedAlpha:true).
precision highp float;
uniform vec2 uResolution;      // piksel perangkat slot
uniform vec2 uViewportOrigin;  // gl_FragCoord relatif ke seluruh framebuffer, bukan viewport (pelajaran prototipe lama)
uniform float uTime;
uniform float uK;              // 0 = tenang (bola), 1 = kusut penuh
uniform float uTaper;          // 0 = bola, >0 = tetesan (puncak runcing)
uniform mat3 uRot;             // koordinat tampilan -> koordinat objek
uniform int uSteps;            // jumlah langkah raymarch (kualitas adaptif)
uniform int uMode;             // 0 tema gelap, 1 tema terang (perlakuan B: transparan, warna tepi dipekatkan)
uniform float uBrightness;     // peredupan anti burn-in (idleBrightness), 0..1
uniform vec3 uLightTint;       // tint badan tema terang (konstanta bernama di orb-morph-motion.ts)
uniform float uLightTintA;     // alpha tint badan tema terang — sengaja SANGAT tipis, bukan badan solid
out vec4 fragColor;

const float R = 0.54;          // radius bola tenang (ruang -1..1 kanvas) => diameter tenang = 54% kanvas
const float RB = 0.93;         // bola pembatas raymarch; deformasi dijepit supaya tidak melewatinya

float hash(vec3 p) {            // pcg3d bilangan bulat (hash float fract(p*17) kehilangan presisi)
  uvec3 u = uvec3(ivec3(p));
  u = u * 1664525u + 1013904223u;
  u.x += u.y * u.z; u.y += u.z * u.x; u.z += u.x * u.y;
  u ^= u >> 16u;
  u.x += u.y * u.z; u.y += u.z * u.x; u.z += u.x * u.y;
  return float(u.x) * (1.0 / 4294967295.0);
}
float vnoise(vec3 x) {          // value noise 3D, kuintik, keluaran -1..1
  vec3 i = floor(x), f = fract(x);
  f = f * f * f * (f * (f * 6.0 - 15.0) + 10.0);
  float a = mix(mix(hash(i), hash(i + vec3(1,0,0)), f.x), mix(hash(i + vec3(0,1,0)), hash(i + vec3(1,1,0)), f.x), f.y);
  float b = mix(mix(hash(i + vec3(0,0,1)), hash(i + vec3(1,0,1)), f.x), mix(hash(i + vec3(0,1,1)), hash(i + vec3(1,1,1)), f.x), f.y);
  return mix(a, b, f.z) * 2.0 - 1.0;
}

vec3 objPos(vec3 p) {           // tampilan -> objek; peremasan pelan + taper tetesan (tanpa pemotongan keras)
  vec3 o = uRot * p;
  o.x *= 1.0 + 0.10 * uK * sin(uTime * 1.9);
  o.y *= 1.0 + 0.10 * uK * sin(uTime * 1.6 + 1.7);
  o.xz *= 1.0 + uTaper * o.y;
  return o;
}
// Medan lipatan HALUS: dua gelombang sinus yang arahnya berputar & fasenya didorong noise (kain sutra),
// ditambah gumpalan noise domain-warped. Semuanya kontinu (tanpa min/max/bidang): tidak ada tepi tegas.
void field(vec3 o, out float lump, out float f1, out float f2) {
  float t = uTime;
  vec3 q = o * 1.05;
  vec3 w = vec3(vnoise(q * 0.8 + vec3(0.0, 0.0, t * 0.28) + 3.1),
                vnoise(q * 0.8 + vec3(t * 0.22, 0.0, 0.0) + 7.7),
                vnoise(q * 0.8 + vec3(0.0, t * 0.18, 0.0) + 11.3));
  lump = vnoise(q + w * 0.9 + vec3(0.0, 0.0, t * 0.34));
  vec3 d1 = normalize(vec3(cos(0.62 * t + 0.5), sin(0.48 * t), 0.7));
  vec3 d2 = normalize(vec3(sin(0.66 * t + 2.0), cos(0.52 * t + 1.0), -0.6));
  f1 = sin(dot(o, d1) * 5.6 + w.x * 2.6 + t * 1.7);
  f2 = sin(dot(o, d2) * 7.8 + w.y * 3.0 - t * 1.4 + lump * 1.5);
}
float map(vec3 p) {
  vec3 o = objPos(p);
  float lump, f1, f2;
  field(o, lump, f1, f2);
  // sqrt(f*f+c): "nilai mutlak" yang DIBULATKAN -> alur lipatan membulat, bukan patah
  float fold = 0.085 * f1 + 0.07 * (sqrt(f2 * f2 + 0.05) - 0.5);
  float disp = mix(0.02, 0.09, uK) * lump + uK * fold;
  disp = clamp(disp, -0.19, 0.17);            // jaminan: siluet tak pernah melewati RB
  return (length(o) - R - disp) * 0.55;       // 0.55 = pengaman Lipschitz karena displasi + taper
}

// Rampa prisma jenuh: 0 cyan-biru -> biru -> magenta -> merah -> oranye -> 1 kuning
vec3 ramp(float t) {
  t = clamp(t, 0.0, 1.0);
  vec3 s0 = vec3(0.00, 0.72, 1.00), s1 = vec3(0.12, 0.22, 1.00), s2 = vec3(0.85, 0.08, 0.60),
       s3 = vec3(0.96, 0.05, 0.06), s4 = vec3(1.00, 0.45, 0.02), s5 = vec3(1.00, 0.88, 0.08);
  float s = t * 5.0;
  if (s < 1.0) return mix(s0, s1, s);
  if (s < 2.0) return mix(s1, s2, s - 1.0);
  if (s < 3.0) return mix(s2, s3, s - 2.0);
  if (s < 4.0) return mix(s3, s4, s - 3.0);
  return mix(s4, s5, s - 4.0);
}
float maxc(vec3 c) { return max(c.r, max(c.g, c.b)); }

void main() {
  vec2 uv = (gl_FragCoord.xy - uViewportOrigin - 0.5 * uResolution) / (0.5 * uResolution.y);
  float px = 2.0 / uResolution.y;
  float r2 = dot(uv, uv);
  bool light = (uMode == 1);

  float minD = 1e3;
  vec3 pMin = vec3(uv, 0.0);
  if (r2 < RB * RB) {
    float zb = sqrt(RB * RB - r2);
    float z = zb;
    for (int i = 0; i < 64; i++) {
      if (i >= uSteps) break;
      vec3 p = vec3(uv, z);
      float d = map(p);
      if (d < minD) { minD = d; pMin = p; }
      if (d < 0.00015) break;
      z -= max(d, 0.0008);
      if (z < -zb) break;
    }
  } else {
    minD = (sqrt(r2) - 0.55) * 0.55;          // hanya untuk pendaran jauh
  }
  float dr = max(minD, 0.0) / 0.55;           // jarak nyata ke permukaan terdekat
  float cov = 1.0 - smoothstep(0.0, px * 1.2, dr);

  float ang = 2.4 + uTime * 0.22;
  vec2 hd = vec2(cos(ang), sin(ang));

  vec3 E = vec3(0.0);
  if (cov > 0.002) {
    vec3 p = pMin;
    vec2 e = vec2(1.0, -1.0) * 0.004;
    vec3 n = normalize(e.xyy * map(p + e.xyy) + e.yyx * map(p + e.yyx) + e.yxy * map(p + e.yxy) + e.xxx * map(p + e.xxx));
    if (minD < 0.004) {                        // proyeksikan ke permukaan sebenarnya, hitung ulang normal (cegah pita berundak)
      p -= n * (map(p) / 0.55);
      n = normalize(e.xyy * map(p + e.xyy) + e.yyx * map(p + e.yyx) + e.yxy * map(p + e.yxy) + e.xxx * map(p + e.xxx));
    }
    float ndv = clamp(n.z, 0.0, 1.0);
    float tilt = 1.0 - ndv;
    float f1w = pow(tilt, light ? 1.9 : 2.6);  // tema terang: cincin tepi dilebarkan supaya terbaca di putih
    float f2w = pow(tilt, light ? 5.0 : 7.0);
    vec3 nObj = uRot * n;

    // sisi hangat (merah-oranye-kuning) mendominasi satu sisi; sisi lain cyan/biru lewat magenta
    float phi = atan(n.y, n.x);
    float h = 0.5 + 0.55 * cos(phi - ang) + 0.16 * sin(2.0 * phi + ang * 0.7) + 0.14 * dot(nObj, vec3(0.5, 0.3, 0.8));
    vec3 rim = vec3(ramp(h + 0.05).r, ramp(h).g, ramp(h - 0.05).b);          // dispersi per kanal
    E = rim * (f1w * (light ? 1.3 : 0.9) + f2w * (light ? 2.4 : 2.0));

    float lump, f1, f2;
    field(objPos(p), lump, f1, f2);
    float fac = 0.5 + 0.5 * sin(dot(nObj, vec3(3.1, 2.3, 1.7)) * 1.6 + lump * 2.5);
    float hh = 0.78 + 0.22 * sin(dot(nObj, vec3(1.3, -2.1, 0.9)) * 1.4 + 1.0 + uTime * 0.3);
    // badan menyala hangat di lekuk yang miring (seperti video); gelap kemerahan di bagian yang menghadap kita
    E += vec3(0.12, 0.015, 0.01) * (0.7 + 0.6 * lump) * (light ? 0.0 : 1.0);
    E += ramp(hh) * uK * smoothstep(0.02, 0.5, tilt) * (0.3 + 0.7 * fac) * (light ? 1.0 : 1.2);
    // goresan kilau PANJANG di sepanjang alur lipatan: garis tempat sinus lipatan melewati nol
    float st2 = exp(-pow(f2 / 0.10, 2.0)) * uK * (0.25 + 0.75 * smoothstep(0.05, 0.6, tilt));
    float st1 = exp(-pow(f1 / 0.08, 2.0)) * uK * (0.2 + 0.8 * smoothstep(0.05, 0.6, tilt));
    E += mix(ramp(hh), vec3(1.0, 0.95, 0.85), 0.6) * st2 * 1.1;
    E += mix(vec3(0.2, 0.7, 1.0), vec3(0.9, 0.97, 1.0), 0.5) * st1 * 0.9;
    // kilau putih: pantulan dua softbox analitik
    vec3 rr = reflect(vec3(0.0, 0.0, -1.0), n);
    float g1 = pow(max(dot(rr, normalize(vec3(-0.5, 0.7, 0.6))), 0.0), 60.0) * 1.2;
    float g2 = pow(max(dot(rr, normalize(vec3(0.7, -0.4, 0.5))), 0.0), 90.0);
    E += vec3(1.0, 0.97, 0.9) * (g1 + g2 * 0.8) * (0.4 + 0.6 * uK) * (light ? 0.5 : 1.0);

    E = E / max(1.0, maxc(E)) * cov;            // pertahankan rona (tonemap per-kanal membuat warna pucat)
  }

  // pendaran merah-oranye di LUAR siluet, dari jarak ke SDF
  vec3 glowCol = mix(vec3(0.95, 0.10, 0.03), vec3(1.0, 0.38, 0.05), 0.5 + 0.5 * dot(normalize(pMin.xy + 1e-4), hd));
  float glowAmt = exp(-dr * 11.0) * 0.30 * (1.0 - cov) * (0.6 + 0.6 * uK) * (1.0 - smoothstep(0.75, 1.0, sqrt(r2)));
  vec3 G = glowCol * glowAmt;

  vec4 outc;   // PREMULTIPLIED (konteks premultipliedAlpha:true)
  if (!light) {
    float bodyA = 0.25 * cov;
    vec3 L = E + G;
    float a = clamp(bodyA + maxc(L), 0.0, 1.0);
    outc = vec4(L + vec3(0.02, 0.0, 0.0) * bodyA, a);
  } else {
    // Terang: tetap transparan. Warna tepi dipekatkan (hue murni, digelapkan), pendaran dalam lebih kuat,
    // plus tint biru-ungu SANGAT tipis (alpha 0,05) di badan supaya orb tidak lenyap di putih; bukan badan solid.
    float me = maxc(E);
    float aE = smoothstep(0.10, 0.55, me);
    float aG = clamp(maxc(G) * 1.1, 0.0, 1.0);
    float aT = uLightTintA * cov;
    vec3 cE = E / max(me, 1e-3) * 0.74;
    vec3 cG = glowCol * 0.8;
    vec3 cT = uLightTint;
    float a = clamp(aE + aG + aT, 0.0, 1.0);
    outc = vec4(min(cE * aE + cG * aG + cT * aT, vec3(a)), a);
  }
  outc.rgb *= uBrightness;   // premultiplied: skala rgb saja, alpha tetap (rgb <= a tetap berlaku)
  // dither segitiga kecil (+-1,5 LSB): memecah pita 8-bit pada gradasi redup/pendaran
  float dn = fract(52.9829189 * fract(dot(gl_FragCoord.xy, vec2(0.06711056, 0.00583715))));
  float dn2 = fract(dn * 7.31 + 0.37);
  float dith = (dn + dn2 - 1.0) * 2.5 / 255.0;
  if (outc.a > 0.0015) { outc.a = clamp(outc.a + dith, 0.0, 1.0); outc.rgb = clamp(outc.rgb + dith, 0.0, outc.a); }
  fragColor = outc;
}`;
